// components/TipTapEditor.tsx
import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { getMarkRange, Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Button, Space, Popover, Input, Checkbox } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  PictureOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import GalleryImagePicker from './GalleryImagePicker';
import { useTheme } from "../../contexts/ThemeProvider";

interface TipTapEditorProps {
  value?: string;
  onChange?: (value: string) => void;
}

const EditorContainer = styled.div`
  border: 1px solid rgba(120, 120, 120, 0.25);
  border-radius: 2px;

  &:focus-within {
    border-color: #d4a574;
    box-shadow: 0 0 0 2px rgba(212, 165, 116, 0.2);
  }
`;

const Toolbar = styled.div<any>`
  padding: 8px;
  background: ${({ isDark }) => isDark ? '#434343' : '#fafafa'};
  border-bottom: 1px solid ${({ isDark }) => isDark ? '#595959' : '#d9d9d9'};
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  position: sticky;
  top: -24px;
  z-index: 1;
`;

const EditorWrapper = styled.div`
  padding: 12px;
  min-height: 400px;
  position: relative;

  .ProseMirror {
    outline: none;

    > * + * {
      margin-top: 0.75em;
    }

    h1 {
      font-size: 2em;
      font-weight: bold;
      margin-top: 0.67em;
      margin-bottom: 0.67em;
    }

    h2 {
      font-size: 1.5em;
      font-weight: bold;
      margin-top: 0.83em;
      margin-bottom: 0.83em;
    }

    h3 {
      font-size: 1.17em;
      font-weight: bold;
      margin-top: 1em;
      margin-bottom: 1em;
    }

    ul, ol {
      padding-left: 2em;
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    blockquote {
      border-left: 3px solid #d9d9d9;
      padding-left: 1em;
      margin-left: 0;
      font-style: italic;
    }

    code {
      background: #f5f5f5;
      padding: 0.2em 0.4em;
      border-radius: 3px;
      font-family: monospace;
    }

    pre {
      background: #f5f5f5;
      padding: 1em;
      border-radius: 4px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
      }
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
    }

    a {
      color: #1890ff;
      text-decoration: underline;
      cursor: pointer;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }

    /* Drag handle styling */
    .drag-handle {
      position: absolute;
      left: -30px;
      cursor: grab;
      opacity: 0;
      transition: opacity 0.2s;
      color: #999;
      font-size: 16px;
      user-select: none;

      &:hover {
        color: #333;
        opacity: 1 !important;
      }

      &:active {
        cursor: grabbing;
      }
    }

    /* Show drag handle on hover */
    p:hover .drag-handle,
    h1:hover .drag-handle,
    h2:hover .drag-handle,
    h3:hover .drag-handle,
    h4:hover .drag-handle,
    h5:hover .drag-handle,
    h6:hover .drag-handle,
    blockquote:hover .drag-handle {
      opacity: 1;
    }

    /* Make blocks draggable */
    p[data-drag-handle],
    h1[data-drag-handle],
    h2[data-drag-handle],
    h3[data-drag-handle],
    h4[data-drag-handle],
    h5[data-drag-handle],
    h6[data-drag-handle],
    blockquote[data-drag-handle] {
      position: relative;
    }

    .ProseMirror-selectednode {
      outline: 2px solid #1890ff;
      border-radius: 4px;
    }
  }
`;

// Handles pasting raw HTML text (e.g., copied from a Claude code block)
const HtmlPasteExtension = Extension.create({
  name: 'htmlPaste',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('htmlPaste'),
        props: {
          handlePaste(view, event) {
            const clipboardText = event.clipboardData?.getData('text/plain') || '';

            // Only intercept when plain text looks like block-level HTML
            const looksLikeHtml = /^[\s\n]*<(h[1-6]|p\b|img\b|ul\b|ol\b|blockquote\b|div\b|section\b|article\b)/i.test(clipboardText.trim());
            if (!looksLikeHtml) return false;

            const dom = document.createElement('div');
            dom.innerHTML = clipboardText;

            const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);
            const slice = parser.parseSlice(dom);

            const { tr } = view.state;
            tr.replaceSelection(slice);
            view.dispatch(tr.scrollIntoView());
            return true;
          },
        },
      }),
    ];
  },
});

// Custom Paragraph with drag handle - using node view
const DraggableParagraph = Paragraph.extend({
  draggable: true,

  addNodeView() {
    return ({ node, HTMLAttributes }: any) => {
      const container = document.createElement('div');
      container.style.position = 'relative';

      const handle = document.createElement('span');
      handle.className = 'drag-handle';
      handle.contentEditable = 'false';
      handle.setAttribute('data-drag-handle', '');
      handle.innerHTML = '⋮⋮';

      const p = document.createElement('p');
      Object.keys(HTMLAttributes).forEach(key => {
        p.setAttribute(key, HTMLAttributes[key]);
      });

      container.appendChild(handle);
      container.appendChild(p);

      return {
        dom: container,
        contentDOM: p,
      };
    };
  },
});

// Custom Heading with drag handle
const DraggableHeading = Heading.extend({
  draggable: true,

  addNodeView() {
    return ({ node, HTMLAttributes }: any) => {
      const level = node.attrs.level || 2;
      const container = document.createElement('div');
      container.style.position = 'relative';

      const handle = document.createElement('span');
      handle.className = 'drag-handle';
      handle.contentEditable = 'false';
      handle.setAttribute('data-drag-handle', '');
      handle.innerHTML = '⋮⋮';

      const heading = document.createElement(`h${level}`);
      Object.keys(HTMLAttributes).forEach(key => {
        heading.setAttribute(key, HTMLAttributes[key]);
      });

      container.appendChild(handle);
      container.appendChild(heading);

      return {
        dom: container,
        contentDOM: heading,
      };
    };
  },
});

// Custom Blockquote with drag handle
const DraggableBlockquote = Blockquote.extend({
  draggable: true,

  addNodeView() {
    return ({ node, HTMLAttributes }: any) => {
      const container = document.createElement('div');
      container.style.position = 'relative';

      const handle = document.createElement('span');
      handle.className = 'drag-handle';
      handle.contentEditable = 'false';
      handle.setAttribute('data-drag-handle', '');
      handle.innerHTML = '⋮⋮';

      const blockquote = document.createElement('blockquote');
      Object.keys(HTMLAttributes).forEach(key => {
        blockquote.setAttribute(key, HTMLAttributes[key]);
      });

      container.appendChild(handle);
      container.appendChild(blockquote);

      return {
        dom: container,
        contentDOM: blockquote,
      };
    };
  },
});

const LinkPopoverContent = ({ editor, onClose }: { editor: any, onClose: () => void }) => {
  const currentAttributes = editor.getAttributes('link');
  const [href, setHref] = useState(currentAttributes.href || '');
  const [openInNewTab, setOpenInNewTab] = useState(currentAttributes.target === '_blank');

  const handleSave = () => {
    if (href) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href, target: openInNewTab ? '_blank' : null })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    onClose();
  };

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return (
    <div style={{ width: '250px' }}>
      <Input
        placeholder="URL"
        value={href}
        onChange={(e) => setHref(e.target.value)}
        onPressEnter={handleSave}
        style={{ marginBottom: '8px' }}
      />
      <Checkbox
        checked={openInNewTab}
        onChange={(e) => setOpenInNewTab(e.target.checked)}
      >
        Open in new tab
      </Checkbox>
      <div style={{ marginTop: '12px', textAlign: 'right' }}>
        <Space size="small">
          <Button onClick={onClose}>Cancel</Button>
          <Button danger onClick={handleRemove}>Remove</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </Space>
      </div>
    </div>
  );
};


function TipTapEditor({ value, onChange }: TipTapEditorProps) {
  const [galleryModalVisible, setGalleryModalVisible] = useState(false);
  const [linkEditorState, setLinkEditorState] = useState<{ visible: boolean; rect: DOMRect; from: number; to: number; } | null>(null);
  const { currentTheme } = useTheme();
  const isDark = currentTheme === 'dark';

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        heading: false,
        blockquote: false,
      }),
      HtmlPasteExtension,
      DraggableParagraph,
      DraggableHeading,
      DraggableBlockquote,
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: null,
          rel: 'noopener noreferrer nofollow',
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        'data-placeholder': 'Write your post content here...',
      },
      transformPastedHTML(html) {
        return html
          .replace(/\s+style="[^"]*"/gi, '')
          .replace(/\s+class="[^"]*"/gi, '');
      },
      handleDOMEvents: {
        mouseover: (view, event) => {
          if (!view.editable) return false;

          const target = event.target as HTMLElement;
          const link = target.closest('a');

          if (link) {
            const rect = link.getBoundingClientRect();
            const pos = view.posAtDOM(link, 0);
            if (pos === null || pos === undefined) return false;

            const $pos = view.state.doc.resolve(pos);
            const range = getMarkRange($pos, view.state.schema.marks.link);

            if (range) {
              const { tr } = view.state;
              view.dispatch(tr.setSelection(new TextSelection(tr.doc.resolve(range.from), tr.doc.resolve(range.to))));
              setLinkEditorState({ visible: true, rect, from: range.from, to: range.to });
            }
            return true;
          }
          return false;
        },
        click: (view, event) => {
          if (!view.editable) return false;

          const target = event.target as HTMLElement;
          const popover = target.closest('.ant-popover');

          if (popover) {
            return true; // Clicked inside popover, do nothing.
          }

          const link = target.closest('a');
          if (link) {
            event.preventDefault(); // Prevent default link behavior
            event.stopPropagation(); // Stop event from propagating
            return true;
          }

          if (linkEditorState?.visible) {
            setLinkEditorState(null);
          }

          return false;
        },
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const handleImageSelect = (imageUrl: string) => {
    editor.chain().focus().setImage({ src: imageUrl }).run();
    setGalleryModalVisible(false);
  };

  const openLinkPopoverForSelection = () => {
    const { from, to, empty } = editor.state.selection;
    if (empty && !editor.isActive('link')) {
      return;
    }

    const { view } = editor;
    const start = view.coordsAtPos(from);
    const end = view.coordsAtPos(to);
    const rect = new DOMRect(start.left, start.top, end.right - start.left, end.bottom - start.top);

    setLinkEditorState({ visible: true, rect, from, to });
  };

  const closeLinkPopover = () => {
    setLinkEditorState(null);
  };

  return (
    <>
      <EditorContainer>
        <Toolbar isDark={isDark}>
          <Space size={4} wrap>
            <Button
              size="small"
              icon={<BoldOutlined />}
              onClick={() => editor.chain().focus().toggleBold().run()}
              type={editor.isActive('bold') ? 'primary' : 'default'}
            />
            <Button
              size="small"
              icon={<ItalicOutlined />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              type={editor.isActive('italic') ? 'primary' : 'default'}
            />
            <Button
              size="small"
              onClick={openLinkPopoverForSelection}
              type={editor.isActive('link') ? 'primary' : 'default'}
              icon={<LinkOutlined />}
            />
            <Button
              size="small"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
            >
              H2
            </Button>
            <Button
              size="small"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              type={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
            >
              H3
            </Button>
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              type={editor.isActive('bulletList') ? 'primary' : 'default'}
            />
            <Button
              size="small"
              icon={<OrderedListOutlined />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              type={editor.isActive('orderedList') ? 'primary' : 'default'}
            />
            <Button
              size="small"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              type={editor.isActive('blockquote') ? 'primary' : 'default'}
            >
              Quote
            </Button>
            <Button
              size="small"
              icon={<PictureOutlined />}
              onClick={() => setGalleryModalVisible(true)}
              type="primary"
            >
              Insert Image
            </Button>
          </Space>
        </Toolbar>

        <EditorWrapper>
          <EditorContent editor={editor} />
        </EditorWrapper>
      </EditorContainer>

      <Popover
        content={<LinkPopoverContent editor={editor!} onClose={closeLinkPopover} />}
        title="Edit Link"
        visible={!!linkEditorState?.visible}
        onVisibleChange={(visible) => {
          if (!visible) closeLinkPopover();
        }}
        destroyTooltipOnHide
      >
        {linkEditorState?.rect && (
            <div
                style={{
                    position: 'fixed',
                    left: `${linkEditorState.rect.left}px`,
                    top: `${linkEditorState.rect.top}px`,
                    width: `${linkEditorState.rect.width}px`,
                    height: `${linkEditorState.rect.height}px`,
                    pointerEvents: 'none',
                }}
            />
        )}
      </Popover>

      <GalleryImagePicker
        visible={galleryModalVisible}
        onSelect={handleImageSelect}
        onCancel={() => setGalleryModalVisible(false)}
      />
    </>
  );
}

export default TipTapEditor;
